/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2025 HG Digital/Setera
 * Todos os direitos reservados.
 *
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 08-01-2025
 * Autor     : System
 * Projeto   : setera-web
 * Versão    : 1.0.0
 * =============================================================================
 */

import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from "@angular/core";
import {
  SupersetService,
  SupersetDashboard,
} from "../../services/superset.service";
import {
  SupersetEmbedService,
  BiIdFilters,
} from "../../services/superset-embed.service";
import { ApiBaseService } from "../../services/migrate/api.base.service";
import { CoreService } from "../../services/migrate/api.core.service";
import {
  STORAGE_KEY_CURRENT_USER,
  STORAGE_KEY_ID_PERFIL,
} from "../../storageCore/constStorageKeys";
import storageX from "../../storageCore/storageX";

/** Opções do filtro "Nome do Evento" — mesmos labels do LATERAL VALUES no dataset Superset. */
export const BI_ALARME_OPTIONS: string[] = [
  "Aceleração brusca",
  "Abastecimento",
  "Alerta de pressão dos pneus",
  "Bateria anormal",
  "Bateria fraca",
  "Curva acentuada",
  "Eventos climáticos",
  "Eventos de furto",
  "Excesso de RPM",
  "Excesso de turbo",
  "Excesso de velocidade",
  "Excesso noturno",
  "Frenagem brusca",
  "Impactos",
  "Superaquecimento",
  "Temperatura de freio",
  "Total de frenagens",
  "Violação de cerca eletrônica",
];

/**
 * Componente responsável por exibir o Business Intelligence (BI) embarcado
 * utilizando o Apache Superset Embedded SDK
 */
@Component({
  selector: "app-bi",
  templateUrl: "./bi.component.html",
  styleUrls: ["./bi.component.scss"],
})
export class BiComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("biContainer", { static: false })
  biContainer: ElementRef<HTMLDivElement>;
  @ViewChild("dashboardMountPoint", { static: false })
  dashboardMountPoint: ElementRef<HTMLDivElement>;

  public isLoading = false;
  public isLoadingDashboards = true;
  public errorMessage: string | null = null;
  public dashboards: SupersetDashboard[] = [];
  public selectedDashboard: SupersetDashboard | null = null;
  public showDashboardList = true;
  public showEmbeddedDashboard = false;
  public isFullscreen = false;
  public isApplyingFilters = false;

  public filterFromDttm = "";
  public filterToDttm = "";
  public empresas: Array<{ id: number; nome: string }> = [];
  public divisoes: Array<{
    id: number;
    nome: string;
    idEmpresaMatriz?: number;
  }> = [];
  public terminais: Array<{
    id: number;
    placa?: string;
    idEmpresa?: number;
    idDivisao?: number;
    nmEmpresa?: string;
    nmDivisao?: string;
  }> = [];
  public divisoesFiltradas: Array<{
    id: number;
    nome: string;
    idEmpresaMatriz?: number;
  }> = [];
  public terminaisFiltrados: Array<{
    id: number;
    placa?: string;
    idEmpresa?: number;
    idDivisao?: number;
    nmEmpresa?: string;
    nmDivisao?: string;
  }> = [];
  public selectedEmpresaIds: number[] = [];
  public selectedDivisaoIds: number[] = [];
  public selectedTerminalIds: number[] = [];
  public motoristas: Array<{
    id: number;
    nome: string;
    idEmpresa?: number;
    nmEmpresa?: string;
  }> = [];
  public motoristasFiltrados: Array<{
    id: number;
    nome: string;
    idEmpresa?: number;
    nmEmpresa?: string;
  }> = [];
  public selectedMotoristaIds: number[] = [];
  public readonly alarmeOptions = BI_ALARME_OPTIONS;
  public selectedAlarmeNames: string[] = [];

  private filtersInitialized = false;

  constructor(
    private supersetService: SupersetService,
    private supersetEmbedService: SupersetEmbedService,
    private apiBaseService: ApiBaseService,
    private coreService: CoreService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadDashboards();
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.supersetEmbedService.unmount();
  }

  public loadDashboards(): void {
    this.isLoadingDashboards = true;
    this.errorMessage = null;

    this.supersetService.listDashboards().subscribe(
      (data) => {
        this.dashboards = (data?.dashboards ?? []).filter((dashboard) => {
          const slug = String(dashboard?.slug ?? "")
            .trim()
            .toLowerCase();
          return slug !== "modulo";
        });
        this.isLoadingDashboards = false;
      },
      (error) => {
        console.error("❌ Erro ao carregar lista de dashboards:", error);
        let errorMsg =
          "Erro ao carregar lista de dashboards. Por favor, tente novamente.";
        if (error?.status === 404) {
          errorMsg =
            "Endpoint de dashboards não encontrado. Verifique se o backend está configurado corretamente.";
        } else if (error?.status === 500) {
          errorMsg =
            "Erro interno do servidor ao buscar dashboards. Verifique os logs do backend.";
        } else if (error?.status === 0 || error?.status === undefined) {
          errorMsg =
            "Não foi possível conectar ao servidor. Verifique se o backend está rodando.";
        }
        this.errorMessage = errorMsg;
        this.isLoadingDashboards = false;
      },
    );
  }

  public selectDashboard(dashboard: SupersetDashboard): void {
    this.selectedDashboard = dashboard;
    this.showDashboardList = false;
    this.showEmbeddedDashboard = true;
    this.filtersInitialized = false;
    this.initDefaultDateFilters();

    setTimeout(() => {
      this.loadDashboard(dashboard);
    }, 100);
  }

  public backToDashboardList(): void {
    this.supersetEmbedService.unmount();
    this.showDashboardList = true;
    this.showEmbeddedDashboard = false;
    this.selectedDashboard = null;
    this.errorMessage = null;
    this.filtersInitialized = false;
  }

  private async loadDashboard(dashboard: SupersetDashboard): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    const mountEl = this.dashboardMountPoint?.nativeElement;
    if (!mountEl) {
      this.errorMessage = "Container do dashboard não encontrado.";
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    if (!dashboard?.embed_id) {
      this.errorMessage =
        "Este dashboard não está habilitado para embed (embed_id ausente). Habilite o embed no Superset ou configure o embed_id no backend.";
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    try {
      await this.ensureFiltersDataLoaded();
      if (!this.filtersInitialized) {
        this.applyDefaultSelections();
        this.filtersInitialized = true;
      }
    } catch (error) {
      console.error("Erro ao carregar filtros do BI:", error);
      this.errorMessage =
        "Erro ao carregar empresas/divisões/terminais/motoristas para o filtro.";
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.supersetEmbedService
      .embedDashboard(dashboard, mountEl, this.buildIdFilters())
      .subscribe(
        () => {
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        (error) => {
          console.error("Erro ao montar dashboard com SDK:", error);
          this.errorMessage =
            "Erro ao carregar o dashboard via SDK. Verifique se o embed está habilitado no Superset e os domínios permitidos.";
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      );
  }

  public reloadDashboard(): void {
    if (this.selectedDashboard) {
      this.loadDashboard(this.selectedDashboard);
    }
  }

  public onEmpresaChange(): void {
    if (this.selectedEmpresaIds.length > 0) {
      // regra solicitada: empresa e divisão não podem ser usados juntos
      this.selectedDivisaoIds = [];
    }
    const empresasSet = new Set(this.selectedEmpresaIds);
    this.divisoesFiltradas = this.selectedEmpresaIds.length
      ? this.divisoes.filter(
          (d) =>
            d.idEmpresaMatriz != null && empresasSet.has(d.idEmpresaMatriz),
        )
      : [...this.divisoes];
    this.recomputeTerminais();
    this.recomputeMotoristas();
  }

  public onDivisaoChange(): void {
    if (this.selectedDivisaoIds.length > 0) {
      // regra solicitada: empresa e divisão não podem ser usados juntos
      this.selectedEmpresaIds = [];
    }
    this.divisoesFiltradas = [...this.divisoes];
    this.recomputeTerminais();
    this.recomputeMotoristas();
  }

  public async applyFilters(): Promise<void> {
    if (!this.canApplyFilters) {
      return;
    }
    this.isApplyingFilters = true;
    try {
      await this.loadDashboard(this.selectedDashboard);
    } finally {
      this.isApplyingFilters = false;
      this.cdr.detectChanges();
    }
  }

  public clearFilters(): void {
    this.initDefaultDateFilters();
    this.selectedTerminalIds = [];
    this.selectedMotoristaIds = [];
    this.applyDefaultSelections();
    this.applyFilters();
  }

  public clearEmpresaSelection(): void {
    this.selectedEmpresaIds = [];
    this.onEmpresaChange();
  }

  public clearDivisaoSelection(): void {
    this.selectedDivisaoIds = [];
    this.onDivisaoChange();
  }

  public clearAlarmeSelection(): void {
    this.selectedAlarmeNames = [];
  }

  public clearTerminalSelection(): void {
    this.selectedTerminalIds = [];
  }

  public clearMotoristaSelection(): void {
    this.selectedMotoristaIds = [];
  }

  public get hasCompanyOrDivisionSelected(): boolean {
    return (
      this.selectedEmpresaIds.length > 0 || this.selectedDivisaoIds.length > 0
    );
  }

  public get canApplyFilters(): boolean {
    return (
      !!this.selectedDashboard &&
      !this.isApplyingFilters &&
      this.hasCompanyOrDivisionSelected
    );
  }

  public get isDriverDashboard(): boolean {
    const title = this.selectedDashboard?.dashboard_title ?? "";
    return /motorista/i.test(title);
  }

  public get isFiveMonthsDashboard(): boolean {
    const title = this.selectedDashboard?.dashboard_title ?? "";
    return /5.*m[eê]s.*anterior.*tr[aá]s/i.test(title);
  }

  public toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }

  private async ensureFiltersDataLoaded(): Promise<void> {
    if (
      this.empresas.length &&
      this.terminais.length &&
      this.motoristas.length
    ) {
      return;
    }

    const profileId = Number(storageX.whereKeyIs(STORAGE_KEY_ID_PERFIL).get());
    const currentUser = storageX.whereKeyIs(STORAGE_KEY_CURRENT_USER).get();
    const userId = Number(currentUser?.id ?? 0);

    const [empresasResp, terminaisResp, motoristasResp] = await Promise.all([
      this.apiBaseService.get<any>({
        url: "empresas/list",
        params: { size: 5000, page: 0, profileId },
      }),
      this.coreService.get<any>({
        url: "v2/terminals/find-terminal",
        params: {
          size: 5000,
          page: 0,
          sort: "dhUltimaAlteracao",
          sortDirections: "DESC",
          profileId,
          userId,
        },
      }),
      this.coreService.get<any>({
        url: "v2/drivers",
        params: { size: 5000, page: 0, profileId },
      }),
    ]);

    const empresasContent = empresasResp?.content ?? [];
    this.empresas = empresasContent
      .filter((item) => item.cnpj != null && String(item.cnpj).trim() !== "")
      .map((item) => ({
        id: Number(item.id),
        nome: String(item.nome),
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    this.divisoes = empresasContent
      .filter((item) => item.cnpj == null || String(item.cnpj).trim() === "")
      .map((item) => ({
        id: Number(item.id),
        nome: String(item.nome),
        idEmpresaMatriz:
          item.idEmpresaMatriz != null
            ? Number(item.idEmpresaMatriz)
            : undefined,
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    const terminaisContent =
      terminaisResp?.content ?? terminaisResp?.elements ?? [];
    this.terminais = terminaisContent
      .map((t) => this.mapTerminalFromApi(t))
      .filter((t) => !!t.id);

    const motoristasContent =
      motoristasResp?.elements ?? motoristasResp?.content ?? [];
    this.motoristas = motoristasContent
      .map((m) => this.mapMotoristaFromApi(m))
      .filter((m) => !!m.id)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    this.divisoesFiltradas = [...this.divisoes];
    this.recomputeTerminais();
    this.recomputeMotoristas();
  }

  private mapMotoristaFromApi(m: Record<string, unknown>): {
    id: number;
    nome: string;
    idEmpresa?: number;
    nmEmpresa?: string;
  } {
    const nmEmpresaRaw = m.nmempresa ?? m.nmEmpresa;

    return {
      id: Number(m.id ?? 0),
      nome: String(m.nmNome ?? m.nome ?? "").trim(),
      idEmpresa:
        m.idEmpresa != null
          ? Number(m.idEmpresa)
          : m.id_empresa != null
            ? Number(m.id_empresa)
            : undefined,
      nmEmpresa:
        nmEmpresaRaw != null && String(nmEmpresaRaw).trim() !== ""
          ? String(nmEmpresaRaw).trim()
          : undefined,
    };
  }

  private mapTerminalFromApi(t: Record<string, unknown>): {
    id: number;
    placa?: string;
    idEmpresa?: number;
    idDivisao?: number;
    nmEmpresa?: string;
    nmDivisao?: string;
  } {
    const nmEmpresaRaw = t.nmempresa ?? t.nmEmpresa;
    const nmDivisaoRaw = t.nmdivisao ?? t.nmDivisao;

    return {
      id: Number(t.id ?? t.idTerminal ?? 0),
      placa: t.placa
        ? String(t.placa)
        : t.plate
          ? String(t.plate)
          : t.cdPlaca
            ? String(t.cdPlaca)
            : undefined,
      idEmpresa:
        t.idEmpresa != null
          ? Number(t.idEmpresa)
          : t.id_empresa != null
            ? Number(t.id_empresa)
            : undefined,
      idDivisao:
        t.idDivisao != null
          ? Number(t.idDivisao)
          : t.id_divisao != null
            ? Number(t.id_divisao)
            : undefined,
      nmEmpresa:
        nmEmpresaRaw != null && String(nmEmpresaRaw).trim() !== ""
          ? String(nmEmpresaRaw).trim()
          : undefined,
      nmDivisao:
        nmDivisaoRaw != null && String(nmDivisaoRaw).trim() !== ""
          ? String(nmDivisaoRaw).trim()
          : undefined,
    };
  }

  private recomputeTerminais(): void {
    if (this.selectedDivisaoIds.length > 0) {
      this.terminaisFiltrados = this.terminais.filter((t) =>
        this.terminalMatchesSelectedDivisoes(t),
      );
    } else if (this.selectedEmpresaIds.length > 0) {
      this.terminaisFiltrados = this.terminais.filter((t) =>
        this.terminalMatchesSelectedEmpresas(t),
      );
    } else {
      this.terminaisFiltrados = [];
    }

    const allowedIds = new Set(this.terminaisFiltrados.map((t) => t.id));
    this.selectedTerminalIds = this.selectedTerminalIds.filter((id) =>
      allowedIds.has(id),
    );
  }

  private terminalMatchesSelectedEmpresas(terminal: {
    idEmpresa?: number;
    idDivisao?: number;
    nmEmpresa?: string;
    nmDivisao?: string;
  }): boolean {
    const empresaIds = new Set(this.selectedEmpresaIds);
    const empresaNames = new Set(
      this.empresas
        .filter((e) => empresaIds.has(e.id))
        .map((e) => this.normalizeName(e.nome)),
    );
    const divisaoIdsVinculadas = new Set(
      this.divisoes
        .filter(
          (d) => d.idEmpresaMatriz != null && empresaIds.has(d.idEmpresaMatriz),
        )
        .map((d) => d.id),
    );
    const divisaoNamesVinculadas = new Set(
      this.divisoes
        .filter((d) => divisaoIdsVinculadas.has(d.id))
        .map((d) => this.normalizeName(d.nome)),
    );

    if (
      terminal.idEmpresa != null &&
      empresaIds.has(terminal.idEmpresa) &&
      terminal.idDivisao == null
    ) {
      return true;
    }

    const nmEmpresa = this.normalizeName(terminal.nmEmpresa);
    if (nmEmpresa && empresaNames.has(nmEmpresa) && !terminal.nmDivisao) {
      return true;
    }

    if (
      terminal.idDivisao != null &&
      divisaoIdsVinculadas.has(terminal.idDivisao)
    ) {
      return true;
    }

    const nmDivisao = this.normalizeName(terminal.nmDivisao);
    if (nmDivisao && divisaoNamesVinculadas.has(nmDivisao)) {
      return true;
    }

    return false;
  }

  private terminalMatchesSelectedDivisoes(terminal: {
    idDivisao?: number;
    nmDivisao?: string;
  }): boolean {
    const divisaoIds = new Set(this.selectedDivisaoIds);
    const divisaoNames = new Set(
      this.divisoes
        .filter((d) => divisaoIds.has(d.id))
        .map((d) => this.normalizeName(d.nome)),
    );

    if (terminal.idDivisao != null && divisaoIds.has(terminal.idDivisao)) {
      return true;
    }

    const nmDivisao = this.normalizeName(terminal.nmDivisao);
    return !!nmDivisao && divisaoNames.has(nmDivisao);
  }

  private normalizeName(value?: string): string {
    return (value ?? "").trim().toLocaleLowerCase("pt-BR");
  }

  private recomputeMotoristas(): void {
    if (this.selectedDivisaoIds.length > 0) {
      this.motoristasFiltrados = this.motoristas.filter((m) =>
        this.motoristaMatchesSelectedDivisoes(m),
      );
    } else if (this.selectedEmpresaIds.length > 0) {
      this.motoristasFiltrados = this.motoristas.filter((m) =>
        this.motoristaMatchesSelectedEmpresas(m),
      );
    } else {
      this.motoristasFiltrados = [];
    }

    const allowedIds = new Set(this.motoristasFiltrados.map((m) => m.id));
    this.selectedMotoristaIds = this.selectedMotoristaIds.filter((id) =>
      allowedIds.has(id),
    );
  }

  private motoristaMatchesSelectedEmpresas(motorista: {
    idEmpresa?: number;
    nmEmpresa?: string;
  }): boolean {
    const empresaIds = new Set(this.selectedEmpresaIds);
    const empresaNames = new Set(
      this.empresas
        .filter((e) => empresaIds.has(e.id))
        .map((e) => this.normalizeName(e.nome)),
    );
    const divisaoIdsVinculadas = new Set(
      this.divisoes
        .filter(
          (d) => d.idEmpresaMatriz != null && empresaIds.has(d.idEmpresaMatriz),
        )
        .map((d) => d.id),
    );
    const divisaoNamesVinculadas = new Set(
      this.divisoes
        .filter((d) => divisaoIdsVinculadas.has(d.id))
        .map((d) => this.normalizeName(d.nome)),
    );

    if (motorista.idEmpresa != null && empresaIds.has(motorista.idEmpresa)) {
      return true;
    }

    if (
      motorista.idEmpresa != null &&
      divisaoIdsVinculadas.has(motorista.idEmpresa)
    ) {
      return true;
    }

    const nmEmpresa = this.normalizeName(motorista.nmEmpresa);
    if (nmEmpresa && empresaNames.has(nmEmpresa)) {
      return true;
    }

    if (nmEmpresa && divisaoNamesVinculadas.has(nmEmpresa)) {
      return true;
    }

    return false;
  }

  private motoristaMatchesSelectedDivisoes(motorista: {
    idEmpresa?: number;
    nmEmpresa?: string;
  }): boolean {
    const divisaoIds = new Set(this.selectedDivisaoIds);
    const divisaoNames = new Set(
      this.divisoes
        .filter((d) => divisaoIds.has(d.id))
        .map((d) => this.normalizeName(d.nome)),
    );

    if (motorista.idEmpresa != null && divisaoIds.has(motorista.idEmpresa)) {
      return true;
    }

    const nmEmpresa = this.normalizeName(motorista.nmEmpresa);
    return !!nmEmpresa && divisaoNames.has(nmEmpresa);
  }

  private buildIdFilters(): BiIdFilters {
    const filters: BiIdFilters = {
      idEmpresa: this.getEffectiveEmpresaIds(),
      idDivisao: this.getEffectiveDivisaoIds(),
      idVeiculo: [],
      eventName: this.getEffectiveAlarmeNames(),
    };

    if (this.isFiveMonthsDashboard) {
      filters.fromMonth = this.formatMonthForSuperset(this.filterFromDttm);
      filters.toMonth = this.formatMonthForSuperset(this.filterToDttm);
    } else {
      filters.fromDttm = this.formatDateTimeForSuperset(
        this.filterFromDttm,
        false,
      );
      filters.toDttm = this.formatDateTimeForSuperset(this.filterToDttm, true);
    }

    if (this.isDriverDashboard) {
      filters.idMotorista = this.getEffectiveMotoristaIds();
    } else {
      filters.idTerminal = this.selectedTerminalIds;
    }

    return filters;
  }

  private getEffectiveMotoristaIds(): number[] {
    const total = this.motoristasFiltrados.length;
    if (
      !this.selectedMotoristaIds.length ||
      (total > 0 && this.selectedMotoristaIds.length >= total)
    ) {
      return [];
    }
    return this.selectedMotoristaIds;
  }

  private getEffectiveAlarmeNames(): string[] {
    if (
      !this.selectedAlarmeNames.length ||
      this.selectedAlarmeNames.length >= BI_ALARME_OPTIONS.length
    ) {
      // url_param vazio no dataset = todos os eventos
      return [];
    }
    return this.selectedAlarmeNames;
  }

  private initDefaultDateFilters(): void {
    const hoje = new Date();
    if (this.isFiveMonthsDashboard) {
      const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth() - 5, 1);
      this.filterFromDttm = this.toMonthInputValue(inicioMes);
      this.filterToDttm = this.toMonthInputValue(hoje);
      return;
    }
    const inicio = new Date(hoje);
    inicio.setDate(inicio.getDate() - 30);

    this.filterFromDttm = this.toDateTimeLocalValue(inicio, false);
    this.filterToDttm = this.toDateTimeLocalValue(hoje, true);
  }

  private applyDefaultSelections(): void {
    this.selectedDivisaoIds = [];
    this.selectedEmpresaIds = this.empresas.map((e) => e.id);
    this.selectedAlarmeNames = [...BI_ALARME_OPTIONS];
    this.onEmpresaChange();
    if (this.isDriverDashboard) {
      this.selectedMotoristaIds = this.motoristasFiltrados.map((m) => m.id);
    } else {
      this.selectedMotoristaIds = [];
    }
  }

  private getEffectiveEmpresaIds(): number[] {
    if (this.selectedDivisaoIds.length > 0) {
      return [];
    }
    if (this.selectedEmpresaIds.length > 0) {
      return this.selectedEmpresaIds;
    }
    return this.empresas.map((e) => e.id);
  }

  private getEffectiveDivisaoIds(): number[] {
    if (this.selectedEmpresaIds.length > 0) {
      return [];
    }
    if (this.selectedDivisaoIds.length > 0) {
      return this.selectedDivisaoIds;
    }
    return this.divisoes.map((d) => d.id);
  }

  private toDateTimeLocalValue(date: Date, endOfDay: boolean): string {
    const d = new Date(date);
    if (endOfDay) {
      d.setHours(23, 59, 0, 0);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private toMonthInputValue(date: Date): string {
    const d = new Date(date);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  }

  private formatMonthForSuperset(value: string): string | undefined {
    if (!value) {
      return undefined;
    }
    const normalized = value.trim();
    if (/^\d{4}-\d{2}$/.test(normalized)) {
      const [year, month] = normalized.split("-");
      return `${month}/${year}`;
    }
    if (/^\d{2}\/\d{4}$/.test(normalized)) {
      return normalized;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) {
      const year = normalized.slice(0, 4);
      const month = normalized.slice(5, 7);
      return `${month}/${year}`;
    }
    return undefined;
  }

  private formatDateTimeForSuperset(
    value: string,
    endOfDay: boolean,
  ): string | undefined {
    if (!value) {
      return undefined;
    }
    if (value.includes("T")) {
      const normalized = value.replace("T", " ");
      return normalized.length === 16 ? `${normalized}:00` : normalized;
    }
    if (value.length === 10) {
      return endOfDay ? `${value} 23:59:59` : `${value} 00:00:00`;
    }
    return value;
  }
}
